/**
 * Formats numbers to display in a more readable format
 * Examples: 40000 -> 40k, 400000 -> 400k, 4000000 -> 4M
 * @param {number} value - The number to format
 * @param {number} decimals - Number of decimal places (default: 0)
 * @returns {string} Formatted number string
 */
export function formatNumber(value, decimals = 0) {
  if (value === null || value === undefined || isNaN(value)) {
    return '';
  }

  const absValue = Math.abs(value);
  
  // For values less than 1000, return as is
  if (absValue < 1000) {
    return value.toString();
  }
  
  // For values 1000-999999, format as k
  if (absValue < 1000000) {
    const formatted = (value / 1000).toFixed(decimals);
    // Remove trailing .0 and .00 for cleaner display
    if (formatted.endsWith('.0')) {
      return formatted.slice(0, -2) + 'k';
    } else if (formatted.endsWith('.00')) {
      return formatted.slice(0, -3) + 'k';
    }
    return formatted + 'k';
  }
  
  // For values 1000000-999999999, format as M
  if (absValue < 1000000000) {
    const formatted = (value / 1000000).toFixed(decimals);
    // Remove trailing .0 and .00 for cleaner display
    if (formatted.endsWith('.0')) {
      return formatted.slice(0, -2) + 'M';
    } else if (formatted.endsWith('.00')) {
      return formatted.slice(0, -3) + 'M';
    }
    return formatted + 'M';
  }
  
  // For values 1000000000+, format as B
  const formatted = (value / 1000000000).toFixed(decimals);
  // Remove trailing .0 and .00 for cleaner display
  if (formatted.endsWith('.0')) {
    return formatted.slice(0, -2) + 'B';
  } else if (formatted.endsWith('.00')) {
    return formatted.slice(0, -3) + 'B';
  }
  return formatted + 'B';
}

/**
 * Creates a Plotly tick format function that formats numbers
 * @param {number} decimals - Number of decimal places
 * @returns {string} Plotly tick format string
 */
export function createPlotlyTickFormat(decimals = 0) {
  return `.${decimals}~f`;
}

/**
 * Creates a custom Plotly tick formatting function for k/M/B notation
 * @param {number} decimals - Number of decimal places
 * @returns {Function} Plotly tick formatting function
 */
export function createCustomTickFormatter(decimals = 0) {
  return function(value) {
    return formatNumber(value, decimals);
  };
}

/**
 * Formats axis values for display in charts
 * @param {Array} values - Array of values to format
 * @param {number} decimals - Number of decimal places
 * @returns {Array} Array of formatted values
 */
export function formatAxisValues(values, decimals = 0) {
  if (!Array.isArray(values)) {
    return [];
  }
  
  return values.map(value => {
    if (typeof value === 'number' && !isNaN(value)) {
      return formatNumber(value, decimals);
    }
    return value;
  });
}

/**
 * Determines if a dataset contains large numbers that should be formatted
 * @param {Array} values - Array of values to check
 * @returns {boolean} True if any value is >= 1000
 */
export function shouldFormatNumbers(values) {
  if (!Array.isArray(values)) {
    return false;
  }
  
  return values.some(value => 
    typeof value === 'number' && !isNaN(value) && Math.abs(value) >= 1000
  );
}
